import {
  Cuepoint,
  LegacyHighlight,
  SectionHighlight,
  SectionHighlights,
} from './types';

export type Section = {
  title: string;
  startTime: number;
};

export function transformKeysToCamelCase(
  obj: Record<string, unknown>,
): Record<string, unknown> | unknown[] {
  if (Array.isArray(obj)) {
    return obj.map((item) => transformKeysToCamelCase(item));
  } else if (obj !== null && obj.constructor === Object) {
    const newObj: Record<string, unknown> = {};
    Object.keys(obj).forEach((key) => {
      const camelCaseKey = key.replace(/(_\w)/g, (match) =>
        match[1].toUpperCase(),
      );
      newObj[camelCaseKey] = transformKeysToCamelCase(
        obj[key] as Record<string, unknown>,
      );
    });
    return newObj;
  }
  return obj;
}

export function sourcesToHighlightsAndCuepoints(
  sectionHighlights: SectionHighlights,
  documentId: string,
) {
  const resultHighlights: LegacyHighlight[] = [];
  const resultCuepoints: Cuepoint[] = [];
  Object.values(sectionHighlights).forEach((section: SectionHighlight) => {
    const { sources, highlights, cuepoints } = section;
    sources.forEach((source) => {
      const sourceId = source.id;
      const roughNotation = source.rough_notation;
      const highlight = highlights.find(
        (highlight) => highlight.source_id === sourceId,
      );
      if (!highlight) {
        console.log(`No highlight found for source with id ${sourceId}`);
        return;
      }

      const cuepoint = cuepoints.find(
        (cuepoint) => cuepoint.highlight_id === highlight.id,
      );
      if (!cuepoint) {
        console.debug(
          `No cuepoint found for highlight with id ${highlight.id}`,
        );
        return;
      }
      const reconstructedHighlight: LegacyHighlight = {
        id: highlight.id,
        ...('selectors' in source ? { selectors: source.selectors } : {}),
        position:
          'zone' in source // html highlight have no position
            ? {
              ...source.zone.position,
              use_pdf_coordinates: Boolean(source.generated),
            }
            : undefined,
        comment: { text: '', emoji: '' },
        content: {
          image:
            'zone' in source && source.zone.position?.rects?.length
              ? '-'
              : undefined,
        },
        // rank: highlight.rank, // not used in legacy logic currently
        weight: highlight.weight,
        rank: 0,
      };
      const reconstructedCuepoint = {
        start: +(cuepoint.start / 1000).toFixed(2),
        highlight_id: cuepoint.highlight_id,
        rough_notation: roughNotation,
        document_id: documentId,
        title: '',
      };
      resultHighlights.push(reconstructedHighlight);
      resultCuepoints.push(reconstructedCuepoint);
    });
  });
  return {
    highlights: {
      document_id: documentId,
      data: resultHighlights,
    },
    cuepoints: {
      data: resultCuepoints,
    },
  };
}

export function extractSectionsFromHighlightAsset(
  sectionMap: { [key: string]: string },
  sectionHighlights: SectionHighlights,
  sectionTimestamps?: { [key: string]: number },
): Section[] {
  const sections: Section[] = [];

  Object.entries(sectionMap).forEach(([sectionId, sectionTitle]) => {
    let sectionStartTime: number;

    if (sectionTimestamps?.[sectionId] !== undefined) {
      sectionStartTime = sectionTimestamps[sectionId] / 1000;
    } else {
      const section = sectionHighlights[sectionId];
      if (!section?.cuepoints?.length) return;

      const firstCuepoint = section.cuepoints[0];
      sectionStartTime =
        (firstCuepoint.start - firstCuepoint.relative_start) / 1000;
    }

    sections.push({
      title: sectionTitle,
      startTime: +sectionStartTime.toFixed(2),
    });
  });

  return sections.sort((a, b) => a.startTime - b.startTime);
}
